import {
  Box,
  Button,
  CircularProgress,
  Grid,
  TextField,
} from "@material-ui/core";
import { Alert, Autocomplete } from "@material-ui/lab";
import {
  useCreateRuleMutation,
  useGetCommoditiesQuery,
  useGetContainersQuery,
  useGetProvidersQuery,
  useUpdateRuleMutation,
} from "../../../services/costs/costsService";
import * as yup from "yup";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { Rule } from "../../../interfaces/costs/costs.rules.interface";
import { IconPlus } from "@tabler/icons-react";
import NewCommodityContainer from "../components/NewCommodityContainer";

const ruleSchema = yup.object().shape({
  naviero: yup.string().required(),
  origen: yup.string().required(),
  destino: yup.string().required(),
  commodity: yup.string().required(),
  contenedor: yup.string().required(),
  contract_number: yup.string().required(),
  notes: yup.string().required(),
});

interface props {
  refecth: () => void;
  ruleToEdit?: Rule;
}

export const LoadingInput = () => {
  return (
    <TextField
      variant="outlined"
      placeholder="Cargando..."
      fullWidth
      disabled
      InputProps={{ endAdornment: <CircularProgress size={20} /> }}
    />
  );
};

const AddNewReglaForm = ({ refecth, ruleToEdit }: props) => {
  const [keyAutoComp, setKeyAutcom] = useState(1);
  const [showNewCommodityForm, setShowNewCommodityForm] = useState(false);

  const handleShowNewCommodityForm = () => {
    setShowNewCommodityForm(true);
  };

  const handleCloseCommodityForm = () => {
    setShowNewCommodityForm(false);
  };

  // Options Autocomplete
  const { isLoading: isLoadingProviders, data: dataProviders } =
    useGetProvidersQuery();

  const { data: dataCommodities, isLoading: isLoadingCommodities } =
    useGetCommoditiesQuery();

  const { isLoading: isLoadingContainers, data: dataContainers } =
    useGetContainersQuery();

  const providersOptions =
    dataProviders?.datos
      .filter((or) => or.nombre !== "UNDEFINED")
      .map((or) => ({
        label: or.nombre,
        value: or.id_naviero,
      })) || [];

  const commoditiesOptions =
    dataCommodities?.datos.map((comm) => ({
      label: comm.nombre,
      value: comm.id_commodity,
    })) || [];

  const containersOptions =
    dataContainers?.datos.map((ct) => ({
      label: ct.nombre,
      value: ct.id_contenedor,
    })) || [];

  const [
    createRule,
    {
      isLoading: isLoadingCreate,
      isSuccess: isSuccessCreate,
      isError: isErrorCreate,
    },
  ] = useCreateRuleMutation();

  const [
    updateRule,
    {
      isLoading: isLoadingUpdate,
      isSuccess: isSuccessUpdate,
      isError: isErrorUpdate,
    },
  ] = useUpdateRuleMutation();

  const {
    handleSubmit,
    errors,
    touched,
    getFieldProps,
    setFieldTouched,
    setFieldValue,
    resetForm,
    values,
  } = useFormik({
    initialValues: {
      naviero: ruleToEdit?.naviero || "",
      origen: ruleToEdit?.origen || "",
      destino: ruleToEdit?.destino || "",
      commodity: ruleToEdit?.commodity || "",
      contenedor: ruleToEdit?.contenedor || "",
      contract_number: ruleToEdit?.contract_number || "",
      notes: ruleToEdit?.notes || "",
    },
    validationSchema: ruleSchema,
    onSubmit: (values) => {
      if (Boolean(ruleToEdit)) {
        updateRule({
          ...values,
          id_regla: Number(ruleToEdit?.id_regla),
        });
        return;
      }
      createRule(values);
    },
  });

  const submit = () => {
    handleSubmit();
  };

  useEffect(() => {
    if (isSuccessCreate === true) {
      refecth();
      resetForm();
      setKeyAutcom((key) => key + 1);
    }
  }, [isSuccessCreate, resetForm, refecth]);

  useEffect(() => {
    if (isSuccessUpdate === true) {
      refecth();
    }
  }, [isSuccessUpdate, refecth]);

  return (
    <div>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          {isLoadingProviders ? (
            <LoadingInput />
          ) : (
            <Autocomplete
              key={keyAutoComp}
              onBlur={() => setFieldTouched("naviero")}
              options={providersOptions}
              getOptionLabel={(opt) => opt.label}
              getOptionSelected={(opt, val) => opt.value === val.value}
              disableClearable={true}
              onChange={(_, val) => {
                setFieldValue("naviero", val.label);
              }}
              disabled={Boolean(ruleToEdit)}
              value={providersOptions.find(
                (opt) => opt.label === values.naviero
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  label="Naviero"
                  error={Boolean(errors.naviero && touched.naviero)}
                  helperText={
                    Boolean(errors.naviero && touched.naviero) &&
                    "Campo Requerido"
                  }
                />
              )}
            />
          )}
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            {...getFieldProps("origen")}
            disabled={Boolean(ruleToEdit)}
            fullWidth
            variant="outlined"
            label="Origen"
            error={Boolean(errors.origen && touched.origen)}
            helperText={
              Boolean(errors.origen && touched.origen) && "Campo Requerido"
            }
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            {...getFieldProps("destino")}
            disabled={Boolean(ruleToEdit)}
            fullWidth
            variant="outlined"
            label="Destino"
            error={Boolean(errors.destino && touched.destino)}
            helperText={
              Boolean(errors.destino && touched.destino) && "Campo Requerido"
            }
          />
        </Grid>
        <Grid item xs={12} md={6} style={{ textAlign: "right" }}>
          <Box component="span">
            <Button
              color="primary"
              variant="text"
              size="small"
              startIcon={<IconPlus />}
              onClick={handleShowNewCommodityForm}
              disabled={Boolean(ruleToEdit)}
            >
              Agregar
            </Button>
            {isLoadingCommodities ? (
              <LoadingInput />
            ) : (
              <Autocomplete
                key={keyAutoComp}
                onBlur={() => setFieldTouched("commodity")}
                options={commoditiesOptions}
                getOptionLabel={(opt) => opt.label}
                getOptionSelected={(opt, val) => opt.value === val.value}
                disableClearable={true}
                onChange={(_, val) => {
                  setFieldValue("commodity", val.label);
                }}
                value={commoditiesOptions.find(
                  (opt) => opt.label === values.commodity
                )}
                disabled={Boolean(ruleToEdit)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    label="Commodity"
                    error={Boolean(errors.commodity && touched.commodity)}
                    helperText={
                      Boolean(errors.commodity && touched.commodity) &&
                      "Campo Requerido"
                    }
                  />
                )}
              />
            )}
          </Box>
        </Grid>
        <Grid item xs={12} md={6} style={{ paddingTop: "40px" }}>
          {isLoadingContainers ? (
            <LoadingInput />
          ) : (
            <Autocomplete
              key={keyAutoComp}
              onBlur={() => {
                setFieldTouched("contenedor");
              }}
              options={containersOptions}
              getOptionLabel={(opt) => opt.label}
              getOptionSelected={(opt, val) => opt.value === val.value}
              disableClearable={true}
              onChange={(_, val) => {
                setFieldValue("contenedor", val.label);
              }}
              value={containersOptions.find(
                (opt) => opt.label === values.contenedor
              )}
              disabled={Boolean(ruleToEdit)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Contenedor"
                  variant="outlined"
                  error={Boolean(errors.contenedor && touched.contenedor)}
                  helperText={
                    Boolean(errors.contenedor && touched.contenedor) &&
                    "Campo Requerido"
                  }
                />
              )}
            />
          )}
        </Grid>
        <Grid item xs={12} md={12}>
          <TextField
            {...getFieldProps("contract_number")}
            label="Contract Number"
            variant="outlined"
            fullWidth
            error={Boolean(errors.contract_number && touched.contract_number)}
            helperText={
              Boolean(errors.contract_number && touched.contract_number) &&
              "Campo Requerido"
            }
            value={values.contract_number}
          />
        </Grid>
        <Grid item xs={12} md={12}>
          <TextField
            {...getFieldProps("notes")}
            label="Notes"
            variant="outlined"
            fullWidth
            multiline
            minRows={4}
            error={Boolean(errors.notes && touched.notes)}
            helperText={
              Boolean(errors.notes && touched.notes) && "Campo Requerido"
            }
            value={values.notes}
          />
        </Grid>
        <Grid item xs={12}>
          {isSuccessCreate && (
            <Alert severity="success">Regla Creada Correctamente</Alert>
          )}
          {isErrorCreate && (
            <Alert severity="error">Ocurrio un error. Intente luego</Alert>
          )}
          {isSuccessUpdate && (
            <Alert severity="success">Regla Actualizada Correctamente</Alert>
          )}
          {isErrorUpdate && (
            <Alert severity="error">Ocurrio un error. Intente luego</Alert>
          )}
        </Grid>
        <Grid item xs={12}>
          <Button
            size="large"
            variant="contained"
            color="primary"
            onClick={submit}
            disabled={isLoadingCreate || isLoadingUpdate}
            fullWidth
          >
            Guardar
          </Button>
        </Grid>
      </Grid>
      <NewCommodityContainer
        isVisible={showNewCommodityForm}
        handleClose={handleCloseCommodityForm}
      />
    </div>
  );
};

export default AddNewReglaForm;
